import { ATTRIBUTE_COLUMNS, METRICS_CONFIG, type MetricConfig } from "@/config/metrics";
import { moneyFormatter } from "@/config/currency";
import { formatMonth, monthOf } from "@/lib/keyword-volume-parser";
import type { CampaignStat } from "@/types/clicks";
import { DERIVED_METRICS } from "./clicksGridEngine";

export const CLICKS_CELL_CLASS =
  "whitespace-nowrap border-r border-border px-4 py-3 last:border-r-0";

export const ALL_METRICS = METRICS_CONFIG.flatMap((g) => g.metrics);

export type MetricFieldId = (typeof ALL_METRICS)[number]["id"];

export type MetricFormat = "int" | "percent" | "currency" | "rate";

/**
 * One middle column of the Clicks grid, in display order. Dimensions that are
 * 1:1 with the grouping (brand, campaign_group) are columns here too, alongside
 * metrics — so they drag, hide and reorder the same way. The grouping key column
 * (dimension) stays pinned first and is not part of this list.
 *
 * ponytail: only 1:1-with-grouping dimensions can be columns, because the server
 * returns one aggregated row per group. Full "any dimension as a column" needs
 * backend support — see the kt-parity brainstorm.
 */
export type GridColumn =
  | { kind: "brand" }
  | { kind: "campaign_group" }
  // A field of the row's domain merged onto the row by id (Domain Tracker);
  // any per-domain dictionary can add its ids here the same way.
  | { kind: "attr"; id: string }
  | { kind: "metric"; metric: MetricConfig };

/** The sortable/drag id for a grid column. */
export const gridColumnKey = (c: GridColumn): string =>
  c.kind === "metric" ? c.metric.id : c.kind === "attr" ? c.id : c.kind;

/** An attribute that is a figure (a count, an amount): right-aligned like the metrics. */
export const isNumericAttribute = (id: string): boolean => {
  const kind = ATTRIBUTE_COLUMNS.get(id)?.kind;
  return kind === "int" || kind === "usd";
};
/** A column that is a per-domain attribute (text, a date, an amount, yes/no), not a metric. */
export const isAttributeColumn = (id: string): boolean => ATTRIBUTE_COLUMNS.has(id);
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const count = new Intl.NumberFormat("en-US");
/** The attribute's raw value on a row (merged onto it on the page). */
export const attributeOf = (row: object, id: string): unknown => (row as Record<string, unknown>)[id];
/** An attribute's value as the cell shows it; null: not set. */
export function formatAttribute(id: string, value: unknown): string | null {
  if (value == null || value === "") return null;
  const kind = ATTRIBUTE_COLUMNS.get(id)?.kind;
  if (kind === "usd" && typeof value === "number") return usd.format(value);
  if (kind === "int" && typeof value === "number") return count.format(value);
  if (kind === "bool" && typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

type MetricDisplayConfig = {
  format: MetricFormat;
  headerTone?: string;
  cellTone?: string;
  valueClass?: string;
  signed?: boolean;
  /** NULL from the server means "unknown", shown as a dash rather than 0. */
  nullable?: boolean;
  /** Tooltip on that dash. */
  nullTitle?: string;
  /** A fall is an improvement (a rank), so the compare delta reads inverted. */
  lowerIsBetter?: boolean;
};

const NO_VOLUME = "No keyword volume: no brand_keyword behind this row, or not in the uploaded exports";

const NO_VOLUME_MONTH = "No keyword volume for this month: exports cover different keywords per month";

// GSC covers ~24 domains. For every other row there is no Search Console data at
// all — that is "not matched", not "zero clicks", so the cell must read "—", not
// 0. (Only meaningful grouped by domain; other groupings never match.)
const NO_GSC = "No Google Search Console data for this row (GSC covers only the connected domains)";

const NO_CAPTURE =
  "No capture rate: needs GSC data and keyword volume for the domain, a fixed date range, and no Country filter (GSC is not split by country here)";

const METRIC_DISPLAY: Record<MetricFieldId, MetricDisplayConfig> = {
  total_clicks: { format: "int", valueClass: "font-medium" },
  unique_campaign: { format: "int", cellTone: "text-muted-foreground" },
  unique_stream: { format: "int", cellTone: "text-muted-foreground" },
  unique_global: { format: "int", cellTone: "text-muted-foreground" },
  lp_clicks: { format: "int" },
  lp_ctr_percent: { format: "percent", cellTone: "text-muted-foreground" },
  total_conversions: { format: "int", valueClass: "font-semibold text-foreground" },
  total_leads: { format: "int", cellTone: "text-foreground/80" },
  total_sales: { format: "int", cellTone: "text-foreground/80" },
  cr_percent: { format: "percent", valueClass: "font-medium" },
  cr_leads_percent: { format: "percent", cellTone: "text-muted-foreground" },
  cr_sales_percent: { format: "percent", cellTone: "text-muted-foreground" },
  total_revenue: {
    format: "currency",
    headerTone: "signal-text-positive",
    valueClass: "font-bold signal-text-positive",
  },
  epc: { format: "rate", valueClass: "font-medium" },
  uepc: { format: "rate", valueClass: "font-medium" },
  uepc_campaign: { format: "rate", cellTone: "text-muted-foreground" },
  approve_percent: { format: "percent" },
  revenue_per_sale: { format: "currency" },
  revenue_per_conversion: { format: "currency", cellTone: "text-muted-foreground" },
  unique_percent: { format: "percent", cellTone: "text-muted-foreground" },
  unique_campaign_percent: { format: "percent", cellTone: "text-muted-foreground" },
  unique_stream_percent: { format: "percent", cellTone: "text-muted-foreground" },
  bots_ua: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_isp: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_device: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_ip_burst: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_ip_visitors: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_language: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_no_browser: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_old_chrome: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  bots_proxy: { format: "int", cellTone: "text-muted-foreground", nullable: true },
  epc_confirmed: { format: "rate", cellTone: "text-muted-foreground" },
  cpc: { format: "rate" },
  ecpc: { format: "rate", cellTone: "text-muted-foreground" },
  ecpm: { format: "rate", cellTone: "text-muted-foreground" },
  ecpm_confirmed: { format: "rate", cellTone: "text-muted-foreground" },
  total_bots: {
    format: "int",
    headerTone: "text-destructive",
    cellTone: "text-destructive",
  },
  bot_share_percent: { format: "percent", cellTone: "text-muted-foreground" },
  not_bots: { format: "int", valueClass: "font-medium", nullable: true },
  bots_oracul: { format: "int", headerTone: "text-destructive", cellTone: "text-destructive", nullable: true },
  bot_share_oracul_percent: { format: "percent", cellTone: "text-muted-foreground", nullable: true },
  gsc_clicks: { format: "int", valueClass: "font-medium", nullable: true, nullTitle: NO_GSC },
  gsc_impressions: { format: "int", cellTone: "text-muted-foreground", nullable: true, nullTitle: NO_GSC },
  gsc_ctr: { format: "percent", cellTone: "text-muted-foreground", nullable: true, nullTitle: NO_GSC },
  // A rank in Google results, not money: plain number (67.6), not "€67.60".
  gsc_position: { format: "int", cellTone: "text-muted-foreground", nullable: true, nullTitle: NO_GSC, lowerIsBetter: true },
  capture_rate: { format: "percent", nullable: true, nullTitle: NO_CAPTURE },
  // keyword volume
  kv_last: { format: "int", valueClass: "font-medium", nullable: true, nullTitle: NO_VOLUME },
  kv_avg: { format: "int", cellTone: "text-muted-foreground", nullable: true, nullTitle: NO_VOLUME },
  kv_trend_percent: { format: "percent", signed: true, nullable: true, nullTitle: NO_VOLUME },
  ...Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [
      `kv_m${i + 1}`,
      { format: "int", cellTone: "text-muted-foreground", nullable: true, nullTitle: NO_VOLUME_MONTH },
    ]),
  ),
};

// Currency lives in one place (src/config/currency.ts) so the whole app shows
// the same money symbol and switching it is a one-line change.
const currencyFmt = moneyFormatter();
const rateFmt = moneyFormatter({
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const DIMENSION_LABELS: Record<string, string> = {
  campaign: "Campaign",
  brand: "Brand",
  domain: "Domain",
  url: "URL",
  offer: "Offer",
  source: "Traffic Source",
  affiliate_network: "Affiliate Network",
  campaign_group: "Campaign Group",
  stream: "Flow (Stream)",
  landing_page: "Landing Page",
  country: "Country",
  region: "Region",
  city: "City",
  language: "Language",
};

/**
 * Brand is offered in the same picker as the metrics because that is where a
 * user looks for a column, but it is text about the row rather than a number
 * measured on it - so it is filtered out here and rendered as its own cell.
 * Left in the list, MetricCell would try to format a brand name as a figure.
 */
export const NON_METRIC_COLUMNS = new Set(["brand", "campaign_group", "kv_sparkline", ...ATTRIBUTE_COLUMNS.keys()]);

// Custom (user-defined) metrics, registered at runtime from localStorage so the
// grid treats them exactly like built-in columns (visible list, format, "—" on a
// null result) without any grid-side changes. Their values are precomputed onto
// each row before render.
export type CustomMetricReg = { id: string; label: string; format: MetricFormat };
let customRegistry: CustomMetricReg[] = [];
export const setCustomMetricRegistry = (list: CustomMetricReg[]): void => {
  customRegistry = list;
};
const NO_CUSTOM =
  "No value: the formula divided by zero, or a metric it needs is missing for this row";

// Column order follows visibleColumns' own order (not ALL_METRICS'), so the grid
// renders metrics in whatever order the user dragged them into — and because
// saved views store visibleColumns, that order persists with the view for free.
export function getVisibleMetrics(visibleColumns: string[]): MetricConfig[] {
  const byId = new Map(ALL_METRICS.map((m) => [m.id, m]));
  const custom = new Map(
    customRegistry.map((c) => [c.id, { id: c.id, label: c.label } as MetricConfig]),
  );
  const out: MetricConfig[] = [];
  for (const id of visibleColumns) {
    if (NON_METRIC_COLUMNS.has(id)) continue;
    const m = byId.get(id) ?? custom.get(id);
    if (m) out.push(m);
  }
  return out;
}

// keyword volume: the newest uploaded month, so kv_m1..kv_m12 can be labelled
// as calendar months. Set once per page load by useVolumeAnchor.
let volumeAnchor: string | undefined;
export const setVolumeAnchor = (anchor: string | undefined) => {
  volumeAnchor = anchor;
};

/** A metric's label; kv_mN becomes its calendar month, e.g. "Volume Aug 2026". */
export function metricLabel(id: string, fallback?: string): string {
  const m = /^kv_m(\d+)$/.exec(id);
  if (m && volumeAnchor) return `Volume ${formatMonth(monthOf(volumeAnchor, Number(m[1])), "en-US")}`;
  const custom = customRegistry.find((c) => c.id === id);
  if (custom) return custom.label;
  return fallback ?? ALL_METRICS.find((x) => x.id === id)?.label ?? id;
}

export function getDimensionLabel(value: string): string {
  return (
    DIMENSION_LABELS[value] ??
    value.charAt(0).toUpperCase() + value.slice(1)
  );
}

export function getMetricDisplay(metricId: string): MetricDisplayConfig {
  const custom = customRegistry.find((c) => c.id === metricId);
  if (custom) return { format: custom.format, nullable: true, nullTitle: NO_CUSTOM };
  return (
    METRIC_DISPLAY[metricId as MetricFieldId] ?? { format: "int" }
  );
}

export function getMetricHeaderTone(metricId: string): string {
  return getMetricDisplay(metricId).headerTone ?? "text-foreground/80";
}

export function getMetricNumericValue(
  row: CampaignStat,
  metricId: MetricFieldId,
): number {
  // uEPC is derived here rather than in SQL: get_dynamic_stats already returns
  // both parts, and adding a column to its RETURNS TABLE means dropping and
  // recreating the busiest function on the page - which also drops its grants.
  // Not worth it for a division.
  //
  // The denominator is unique_global, one per visitor however they arrived,
  // because that is the question uEPC answers: what a person is worth. The
  // per-campaign and per-stream counts reset at their own boundary, so the same
  // person is counted again in the next campaign and the figure drifts up.
  const derived = DERIVED_METRICS[metricId];
  if (derived) return derived(row);

  const raw = row[metricId as keyof CampaignStat];
  return typeof raw === "number" ? raw : 0;
}

export function formatMetricValue(
  value: number,
  format: MetricFormat,
): string {
  const safe = value || 0;
  switch (format) {
    case "currency":
      return currencyFmt.format(safe);
    case "rate":
      return rateFmt.format(safe);
    case "percent":
      return `${Number(safe).toFixed(2)}%`;
    case "int":
    default:
      // Counts are whole; a formula's plain-number result is not (9.666666…).
      // Thousandths are enough on screen, trailing zeros dropped (9.667, 7.6).
      // CSV export keeps the raw value.
      return Number.isInteger(safe) ? String(safe) : String(Math.round(safe * 1000) / 1000);
  }
}

const METRIC_NUMERIC_BASE = "tabular-nums tracking-tight";

export function getMetricValueClass(
  metricId: string,
  value: number,
): string {
  const config = getMetricDisplay(metricId);
  if (config.signed) {
    const positive = (value || 0) >= 0;
    return [
      METRIC_NUMERIC_BASE,
      config.valueClass,
      positive ? "signal-text-positive" : "signal-text-negative",
    ]
      .filter(Boolean)
      .join(" ");
  }
  return [METRIC_NUMERIC_BASE, config.valueClass].filter(Boolean).join(" ");
}

export function getMetricCellTone(metricId: string): string | undefined {
  return getMetricDisplay(metricId).cellTone;
}
